import React, { useState, useEffect } from "react";
import {
  Home,
  Users,
  Settings,
  BarChart2,
  Layers,
  ChevronLeft,
  Menu,
  LogOut,
  PenTool,
  Mic,
  Cloud,
  Smile,
  XCircle,
  Search,
  // Icons used for the expanded menu structure:
  Gamepad,
  BookOpen,
  Music,
  Gift,
  Trophy,
  Video,
} from "lucide-react";

// NOTE: This Link component simulates routing/navigation for a single-file application.
const Link = ({ to, children, className, onClick, title }) => (
  <a href={to} className={className} onClick={onClick} title={title}>
    {children}
  </a>
);

// --- 1. Menu Data ---

const analysisItems = [
  {
    name: "Game Analytics",
    icon: Gamepad,
    path: "/analysis/games",
    current: false,
    detail: "Performance in Play Games & Puzzle Corner",
  },
  {
    name: "Story & Reading",
    icon: BookOpen,
    path: "/analysis/story",
    current: false,
    detail: "Engagement in Story Time & Learning Tips",
  },
  {
    name: "Creation Stats",
    icon: PenTool,
    path: "/analysis/creative",
    current: false,
    detail: "Usage of Create & Draw & Workshops",
  },
  {
    name: "Music Engagement",
    icon: Music,
    path: "/analysis/music",
    current: false,
    detail: "Activity in Music & Dance",
  },
  {
    name: "Parent & Skill",
    icon: Users,
    path: "/analysis/learning",
    current: false,
    detail: "Parent Corner & Skill Development usage",
  },
  {
    name: "Store Performance",
    icon: Gift,
    path: "/analysis/store",
    current: false,
    detail: "Merchandise Store traffic & sales",
  },
  {
    name: "Event & Contest",
    icon: Trophy,
    path: "/analysis/events",
    current: false,
    detail: "Webinar & Competitions attendance",
  },
];

const coreItems = [
  { name: "Dashboard", icon: Home, path: "/", current: true },
  { name: "My Toons", icon: Cloud, path: "/my-toons", current: false },
  { name: "Voice Studio", icon: Mic, path: "/voice-studio", current: false },
];

const secondaryItems = [
  {
    name: "Profile Settings",
    icon: Settings,
    path: "/settings",
    current: false,
  },
  { name: "Logout", icon: LogOut, path: "/logout", current: false },
];

// ------------------------------------------------------------------
// --- 2. Menu Item Component ---
// ------------------------------------------------------------------
const MenuItem = ({ name, icon: Icon, path, isExpanded, isCurrent }) => {
  const isActive = isCurrent;

  const baseClasses =
    "flex items-center p-3 rounded-xl transition-all duration-300 group";
  const activeClasses =
    "bg-purple-600 text-white shadow-lg hover:bg-purple-700";
  const inactiveClasses =
    "text-gray-700 hover:bg-purple-50 hover:text-purple-600";

  // Tooltip for collapsed state (desktop only)
  const tooltipClasses = isExpanded
    ? "hidden"
    : "absolute left-20 ml-4 py-1 px-3 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-50";

  return (
    <div className="relative">
      <Link
        to={path}
        className={`${baseClasses} ${
          isActive ? activeClasses : inactiveClasses
        }`}
        title={name}
      >
        <Icon className="w-6 h-6 flex-shrink-0" />
        <span
          className={`ml-4 overflow-hidden whitespace-nowrap font-medium 
                        ${
                          isExpanded
                            ? "block opacity-100 lg:block"
                            : "opacity-0 hidden lg:hidden"
                        }`}
        >
          {name}
        </span>
      </Link>
      {/* Tooltip visible only when collapsed on large screens */}
      <span className={tooltipClasses}>{name}</span>
    </div>
  );
};

// ------------------------------------------------------------------
// --- 3. Menu Section Component ---
// ------------------------------------------------------------------
const MenuSection = ({ title, items, isExpanded }) => (
  <div className="space-y-1">
    <h3
      className={`text-xs font-bold uppercase tracking-widest text-gray-400 px-3 pt-4 pb-2 
              transition-opacity duration-300 ${
                isExpanded
                  ? "opacity-100 lg:block"
                  : "opacity-0 hidden lg:hidden"
              }`}
    >
      {title}
    </h3>
    {items.map((item, index) => (
      <MenuItem
        key={index}
        {...item}
        isExpanded={isExpanded}
        isCurrent={item.current}
      />
    ))}
  </div>
);

// ------------------------------------------------------------------
// --- 4. Sidebar Component ---
// ------------------------------------------------------------------
const Sidebar = ({ isExpanded, toggleSidebar, isMobileOpen, toggleMobile }) => {
  // Dynamic width class based on isExpanded state for desktop (lg screen)
  const widthClass = isExpanded ? "lg:w-64" : "lg:w-20";
  // Mobile visibility controlled by isMobileOpen
  const mobileTransformClass = isMobileOpen
    ? "translate-x-0"
    : "-translate-x-full";

  return (
    <>
      {/* --- Mobile Overlay --- */}
      <div
        className={`fixed inset-0 bg-black/50 z-30 transition-opacity duration-300 lg:hidden ${
          isMobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleMobile}
      />

      {/* --- Sidebar Content --- */}
      <aside
        className={`fixed inset-y-0 left-0 bg-white shadow-2xl z-40 transition-all duration-300 
                    w-64 flex flex-col p-4 space-y-6 ${widthClass} ${mobileTransformClass} lg:translate-x-0`}
      >
        {/* Header/Logo */}
        <div className="flex items-center justify-between h-16 relative">
          <div className="flex items-center space-x-2 overflow-hidden">
            <Mic className="w-8 h-8 text-purple-600 flex-shrink-0" />
            <span
              className={`text-2xl font-black text-gray-900 transition-opacity duration-300 
                                ${
                                  isExpanded
                                    ? "opacity-100"
                                    : "opacity-0 hidden lg:inline"
                                }`}
            >
              TALKYTOON
            </span>
          </div>

          {/* Collapse Button (Desktop) */}
          <button
            onClick={toggleSidebar}
            className={`absolute -right-6 top-1/2 transform -translate-y-1/2 p-1 bg-white border border-gray-200 text-gray-500 rounded-full shadow-md hover:bg-purple-100 hover:text-purple-600 transition-all duration-300 hidden lg:block`}
            aria-label="Toggle Sidebar"
          >
            <ChevronLeft
              className={`w-5 h-5 transition-transform duration-300 ${
                isExpanded ? "" : "rotate-180"
              }`}
            />
          </button>

          {/* Close Button (Mobile) */}
          <button
            onClick={toggleMobile}
            className="p-1 text-gray-500 hover:bg-gray-100 rounded-full transition-colors lg:hidden"
            aria-label="Close Mobile Menu"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-grow space-y-6 overflow-y-auto">
          <MenuSection title="Main" items={coreItems} isExpanded={isExpanded} />

          {/* New Analysis Hub Section */}
          <div className="pt-6 border-t border-gray-100">
            <MenuSection
              title="Analysis Hub"
              items={analysisItems}
              isExpanded={isExpanded}
            />
          </div>

          <div className="pt-6 border-t border-gray-100">
            <MenuSection
              title="System"
              items={secondaryItems}
              isExpanded={isExpanded}
            />
          </div>
        </nav>
      </aside>
    </>
  );
};

// ------------------------------------------------------------------
// --- 5. Card Helper Component ---
// ------------------------------------------------------------------
const Card = ({ title, content, color, icon: Icon }) => {
  // Tailwind classes for dynamic colors
  const colorClasses = {
    indigo: "bg-indigo-600/10 text-indigo-800 border-indigo-500",
    yellow: "bg-yellow-600/10 text-yellow-800 border-yellow-500",
    green: "bg-green-600/10 text-green-800 border-green-500",
    purple: "bg-purple-600/10 text-purple-800 border-purple-500",
  };

  return (
    <div
      className={`p-6 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 cursor-pointer border-l-4 ${colorClasses[color]}`}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-xl font-extrabold">{title}</h3>
        <Icon className="w-7 h-7 opacity-70" />
      </div>
      <p className="text-sm font-medium opacity-90">{content}</p>
    </div>
  );
};

// --- Analysis Report Item Component ---
const AnalysisReportItem = ({ icon: Icon, name, detail, path }) => {
  return (
    <Link
      to={path}
      className="flex items-center p-4 rounded-xl border border-gray-200 bg-white hover:bg-purple-50 transition-all duration-200"
      title={`Go to ${name} Report`}
    >
      <div className="p-3 rounded-full bg-purple-100 text-purple-600 flex-shrink-0 mr-4">
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-lg font-semibold text-gray-800">{name}</p>
        <p className="text-sm text-gray-500">{detail}</p>
      </div>
    </Link>
  );
};

// ------------------------------------------------------------------
// --- 6. Main App Component ---
// ------------------------------------------------------------------
export default function App() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleDesktopSidebar = () => setIsExpanded(!isExpanded);
  const toggleMobileSidebar = () => setIsMobileOpen(!isMobileOpen);

  const mainContentMarginClass = isExpanded ? "lg:ml-64" : "lg:ml-20";
  const headerLeftOffsetClass = isExpanded ? "lg:left-64" : "lg:left-20";

  return (
    <div className="min-h-screen bg-gray-100 flex font-sans">
      {/* 7. Sidebar Component */}
      <Sidebar
        isExpanded={isExpanded}
        toggleSidebar={toggleDesktopSidebar}
        isMobileOpen={isMobileOpen}
        toggleMobile={toggleMobileSidebar}
      />

      {/* --- Main Content Area Wrapper --- */}
      <div
        className={`flex-grow transition-all duration-300 ${mainContentMarginClass}`}
      >
        {/* --- Fixed Top Bar (Header/Navbar) --- */}
<header
  className={`fixed top-0 right-0 ${headerLeftOffsetClass} 
              h-auto bg-white shadow-lg z-30 flex flex-col md:flex-row 
              items-center px-4 sm:px-8 py-3 transition-all duration-300`}
>
  <div className="flex items-center w-full md:w-auto">

    {/* Mobile Menu Button */}
    <button
      onClick={toggleMobileSidebar}
      className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors lg:hidden"
      aria-label="Open Mobile Menu"
    >
      <Menu className="w-6 h-6" />
    </button>

    {/* Title */}
    <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 ml-3 md:ml-0">
      Central Analysis Dashboard
    </h1>

    {/* Right section (Profile) for mobile stays right aligned */}
    <div className="ml-auto flex items-center space-x-3 md:hidden">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-semibold text-gray-700 transition-colors">
          Admin View
        </span>
        <div className="w-9 h-9 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold shadow-md ring-2 ring-purple-300">
          <BarChart2 className="w-5 h-5" />
        </div>
      </div>
    </div>
  </div>

  {/* Search + Profile on Desktop */}
  <div className="flex items-center w-full md:w-auto mt-3 md:mt-0 md:ml-auto space-x-4">

    {/* Responsive Search */}
    <div className="relative w-full md:w-64">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
      <input
        type="text"
        placeholder="Search reports..."
        className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-full 
                   focus:ring-purple-500 focus:border-purple-500 transition"
      />
    </div>

    {/* User Profile Desktop */}
    <div className="hidden md:flex items-center space-x-2 cursor-pointer group">
      <span className="text-sm font-semibold text-gray-700 transition-colors group-hover:text-purple-600">
        Admin View
      </span>
      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center font-bold text-white shadow-md ring-2 ring-purple-300">
        <BarChart2 className="w-6 h-6" />
      </div>
    </div>

  </div>
</header>

        {/* --- Main Content Cards/Data (Analysis Focus) --- */}
        <main className="pt-24 px-4 sm:px-8">
          {" "}
          {/* Padding to offset the fixed header */}
          <h2 className="text-3xl font-extrabold text-gray-900 mb-8">
            Global Platform Performance Summary
          </h2>
          {/* Stats Cards: Key Analysis Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <Card
              title="Play Games Installs"
              content="1,245 new players this week (+15%)."
              color="purple"
              icon={Gamepad}
            />
            <Card
              title="Story Time Completion"
              content="88% average chapter completion rate."
              color="green"
              icon={BookOpen}
            />
            <Card
              title="Merch Store Sales"
              content="$4,500 in revenue last 30 days."
              color="indigo"
              icon={Gift}
            />
            <Card
              title="Webinar Attendance"
              content="250 attendees for the latest session."
              color="yellow"
              icon={Video}
            />
          </div>
          {/* Detailed Analysis Links (Mapping all requested menu items) */}
          <div className="mt-10 bg-white p-8 rounded-2xl shadow-xl border border-gray-200">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">
              Deep Dive Analysis Reports
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analysisItems.map((item, index) => (
                <AnalysisReportItem
                  key={index}
                  {...item}
                  icon={item.icon}
                  name={item.name}
                  detail={item.detail}
                  path={item.path}
                />
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
